<template>
  <div>
    <EquipmentEditHeader
      v-if="isAdmin"
      data-cy="step-down-transformer-search-view-edit-header"
    />

    <div v-else class="mb-3">
      <div class="flex align-items-center gap-4">
        <div v-if="isHps" class="flex align-items-center field-checkbox">
          <Checkbox
            v-model="enableFilter"
            input-id="enable-step-down-filter"
            binary
          />
          <label for="enable-step-down-filter">
            Filter and Sort Transformers
          </label>
        </div>
      </div>
    </div>

    <div
      v-if="noContextForFilter"
      class="no-recommendation-message-container"
      data-cy="step-down-no-context-message"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No transformer recommendations can be made.
        </p>
        <p class="no-recommendation-body">
          Please select a motor to see step-down transformer recommendations, or
          un-check
          <span class="no-recommendation-highlight">
            "Filter and Sort Transformers"
          </span>
          to view all options.
        </p>
      </div>
    </div>

    <div
      v-else-if="noStepDownsWithFilter"
      class="no-recommendation-message-container"
      data-cy="step-down-no-results-message"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No step-down transformers match this configuration.
        </p>
        <p class="no-recommendation-body">
          If you'd like to select a transformer, please un-check
          <span class="no-recommendation-highlight">
            "Filter and Sort Transformers"
          </span>
          to view all available options.
        </p>
      </div>
    </div>

    <EquipmentTable
      v-else
      data-cy="step-down-transformer-search-view-table"
      @select="$emit('select', $event.data as Step_Down_Transformers)"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, provide, ref, inject, watch } from "vue";
import { useStepDownTransformers } from "@/composables/equipment/stepDownTransformers/sdts";
import EquipmentEditHeader from "@/components/equipment/EquipmentEditHeader.vue";
import EquipmentTable from "@/components/equipment/EquipmentTable.vue";
import type { Step_Down_Transformers } from "gql/api";
import Checkbox from "primevue/checkbox";
import {
  equipmentFormKey,
  parametersFormKey,
  scenarioCalculationsKey,
  scenarioFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const props = defineProps<{
  isAdmin: boolean;
}>();

defineEmits<{
  (event: "select", data: Step_Down_Transformers): void;
}>();

const sdtsearch = useStepDownTransformers(props.isAdmin);

const scenarioForm = !props.isAdmin ? inject(scenarioFormKey, null) : null;
const scenarioCalculations = !props.isAdmin
  ? inject(scenarioCalculationsKey, null)
  : null;
const equipmentForm = !props.isAdmin ? inject(equipmentFormKey, null) : null;
const parametersForm = !props.isAdmin ? inject(parametersFormKey, null) : null;

const isHps = computed(() => {
  if (!scenarioForm) return false;
  return scenarioForm.data.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});

const enableFilter = ref<boolean>(isHps.value);

// Ensure HPS scenarios default to having recommendations enabled even if
// scenario data loads asynchronously, without overriding user toggles.
watch(
  isHps,
  value => {
    if (value && !enableFilter.value) {
      enableFilter.value = true;
    }
  },
  { immediate: false }
);

const isMotorSelected = computed(() => {
  if (!equipmentForm) return false;
  const eq = equipmentForm.data.value;
  return !!(eq.motor_id || eq.lower_motor_id);
});

const sortedStepDowns = computed(() => {
  const rows = sdtsearch.allRows.value;

  if (!rows) {
    return rows;
  }

  if (props.isAdmin || !isHps.value || !enableFilter.value) {
    return rows;
  }

  if (!isMotorSelected.value) {
    return [];
  }

  if (!scenarioCalculations) {
    return rows;
  }

  // Step-down secondary is the low-voltage/utilization side.
  // For VSD configs, use VSD input line voltage; otherwise fall back to motor side.
  const motorCalcs = scenarioCalculations.motorCalculations;
  const motorVoltageForRange =
    motorCalcs.reratedNameplateV.value || motorCalcs.voltageNameplate.value;
  const isUsingVsd = !!equipmentForm?.data.value?.use_vsd;
  const stepDownVoltageForRange = isUsingVsd
    ? Number(parametersForm?.data.value?.vsd_input_voltage || 0)
    : motorVoltageForRange;
  const surfaceDriveCalcs = scenarioCalculations.surfaceDriveCalculations;
  const requiredKva =
    surfaceDriveCalcs.requiredKVA60Hz.value ||
    surfaceDriveCalcs.requiredKVA.value;
  // Prefer catalog kVA <= 1.25× required, but discrete standard sizes often skip
  // that band — if nothing qualifies, fall back to min kVA + tap range only.
  const maxKvaPreferred = requiredKva * 1.25;

  if (!stepDownVoltageForRange || !requiredKva) {
    return rows;
  }

  const collectPassing = (usePreferredMaxKva: boolean) => {
    const out: Array<{
      row: Step_Down_Transformers;
      kva: number;
      secondaryCenter: number;
      secondarySpan: number;
    }> = [];

    rows.forEach(row => {
      const kva = row.kva;
      const minSec = row.min_secondary_voltage;
      const maxSec = row.max_secondary_voltage;

      if (
        kva === null ||
        kva === undefined ||
        minSec === null ||
        minSec === undefined ||
        maxSec === null ||
        maxSec === undefined
      ) {
        return;
      }

      const withinTapRange = !!(
        stepDownVoltageForRange >= minSec && stepDownVoltageForRange <= maxSec
      );

      const kvaOk =
        withinTapRange &&
        kva >= requiredKva &&
        (!usePreferredMaxKva || kva <= maxKvaPreferred);

      if (kvaOk) {
        const secondaryCenter = (minSec + maxSec) / 2;
        const secondarySpan = maxSec - minSec;
        out.push({ row, kva, secondaryCenter, secondarySpan });
      }
    });

    return out;
  };

  let passing = collectPassing(true);
  if (passing.length === 0) {
    passing = collectPassing(false);
  }

  if (passing.length === 0) {
    return [];
  }

  // Rank by voltage fit first, then by lowest passing kVA.
  passing.sort((a, b) => {
    const aDiff = Math.abs(a.secondaryCenter - stepDownVoltageForRange);
    const bDiff = Math.abs(b.secondaryCenter - stepDownVoltageForRange);
    if (aDiff !== bDiff) return aDiff - bDiff;
    if (a.kva !== b.kva) return a.kva - b.kva;
    return a.secondarySpan - b.secondarySpan;
  });

  return passing.map(entry => entry.row);
});

const sortedFilteredRows = computed(() => {
  const baseFiltered = sdtsearch.filteredRows.value;
  const sorted = sortedStepDowns.value;

  if (!baseFiltered || !sorted) {
    return sorted as typeof baseFiltered;
  }

  const filteredIds = new Set(baseFiltered.map(p => p.id));
  return sorted.filter(stepDown => filteredIds.has(stepDown.id));
});

const noContextForFilter = computed(() => {
  const filterOn = enableFilter.value && isHps.value && !props.isAdmin;
  const hasLoadedRows = Array.isArray(sdtsearch.allRows.value);
  return (
    filterOn &&
    hasLoadedRows &&
    !isMotorSelected.value
  );
});

const noStepDownsWithFilter = computed(() => {
  const filterOn = enableFilter.value && isHps.value && !props.isAdmin;
  const count = sortedFilteredRows.value?.length ?? 0;
  const hasLoadedRows = Array.isArray(sdtsearch.allRows.value);
  return (
    filterOn &&
    hasLoadedRows &&
    count === 0 && isMotorSelected.value
  );
});

const equipmentWithSortedStepDowns = {
  ...sdtsearch,
  allRows: sortedStepDowns,
  filteredRows: sortedFilteredRows
};

provide("equipment", equipmentWithSortedStepDowns);
</script>
